import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Copy, CheckCircle, Users, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ReferralProgressProps {
  referralsCount: number;
  hasSubscription: boolean;
}

const GOAL = 3;

const ReferralProgress: React.FC<ReferralProgressProps> = ({ referralsCount, hasSubscription }) => {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (hasSubscription) {
      fetchReferralCode();
    }
  }, [hasSubscription]);

  const fetchReferralCode = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .rpc('get_affiliate_stats', { user_id_param: user.id });

      if (data?.[0]?.referral_code) {
        setReferralCode(data[0].referral_code);
      }
    } catch (err) {
      console.error('Error fetching referral code:', err);
    }
  };

  const copyLink = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/register?ref=${referralCode}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying:', err);
    }
  };

  // No subscription = don't show
  if (!hasSubscription) return null;

  const progress = Math.min(referralsCount, GOAL);
  const percentage = Math.round((progress / GOAL) * 100);
  const covered = referralsCount >= GOAL;

  return (
    <div className={`card border ${covered ? 'border-green-500/30' : 'border-primary/20'}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Left: progress info */}
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <div className={`p-2 rounded-lg ${covered ? 'bg-green-500/10' : 'bg-primary/10'}`}>
              <Users className={`h-5 w-5 ${covered ? 'text-green-400' : 'text-primary'}`} />
            </div>
            <div>
              {covered ? (
                <p className="text-green-400 font-semibold text-sm">Tu suscripcion esta cubierta</p>
              ) : (
                <p className="text-white font-semibold text-sm">
                  {progress} / {GOAL} referidos para cubrir tu suscripcion
                </p>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-white/10 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${covered ? 'bg-green-500' : 'bg-primary'}`}
              style={{ width: `${percentage}%` }}
            />
          </div>

          <p className="text-xs text-gray-500">
            {covered
              ? `Tienes ${referralsCount} referidos activos. Cada nuevo referido es ingreso extra.`
              : referralsCount === 0
                ? 'Comparte tu enlace con otros traders que operen Nasdaq.'
                : `Te ${GOAL - progress === 1 ? 'falta 1 referido' : `faltan ${GOAL - progress} referidos`} mas.`
            }
          </p>
        </div>

        {/* Right: copy button */}
        <div className="flex flex-col gap-2 sm:items-end shrink-0">
          {referralCode ? (
            <button
              onClick={copyLink}
              className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                copied
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-primary/10 text-primary hover:bg-primary/20'
              }`}
            >
              {copied ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Copiado</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  <span>Copiar enlace</span>
                </>
              )}
            </button>
          ) : (
            <Link
              to="/dashboard/affiliates"
              className="inline-flex items-center space-x-2 text-primary text-sm hover:text-primary-hover"
            >
              <span>Ver programa</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralProgress;
